import { useEffect, useState } from "react";
import { Btn, Modal } from "../../ui";
import type { PairingCreatedResponse } from "../../api";

export interface PairingState {
  pairing: PairingCreatedResponse;
  remainingMs: number;
}

interface PairingModalProps {
  open: boolean;
  pairing: PairingState | null;
  pairLabel: string;
  pairBusy: boolean;
  onClose: () => void;
  onCancelPairing: () => void;
  onClosePairing: () => void;
  onGeneratePairing: () => void;
  onLabelChange: (value: string) => void;
}

export function PairingModal({
  open,
  pairing,
  pairLabel,
  pairBusy,
  onClose,
  onCancelPairing,
  onClosePairing,
  onGeneratePairing,
  onLabelChange,
}: PairingModalProps) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  const expired = pairing !== null && pairing.remainingMs <= 0;
  useEffect(() => { setCopyState("idle"); }, [pairing?.pairing.id]);

  const copyCode = async () => {
    if (!pairing || expired) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard non disponibile");
      await navigator.clipboard.writeText(pairing.pairing.code);
      setCopyState("copied");
    } catch {
      setCopyState("error");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={pairing ? "Codice abbinamento" : "Nuovo abbinamento"}
      footer={
        pairing ? (
          <>
            <Btn variant="ghost" disabled={pairBusy} onClick={onCancelPairing}>Annulla codice</Btn>
            <Btn variant={expired ? "ghost" : "primary"} disabled={pairBusy} onClick={onClosePairing}>Chiudi</Btn>
            {expired && (
              <Btn variant="primary" disabled={pairBusy} onClick={onGeneratePairing}>
                {pairBusy ? "Generazione…" : "Genera nuovo codice"}
              </Btn>
            )}
          </>
        ) : (
          <>
            <Btn variant="ghost" disabled={pairBusy} onClick={onClose}>Annulla</Btn>
            <Btn variant="primary" disabled={pairBusy} onClick={onGeneratePairing}>
              {pairBusy ? "Generazione…" : "Genera codice"}
            </Btn>
          </>
        )
      }
    >
      {!pairing ? (
        <>
          <div className="field">
            <label className="label">Nome del computer (facoltativo)</label>
            <input
              className="input"
              placeholder="es. PC Mario"
              value={pairLabel}
              disabled={pairBusy}
              onChange={(e) => onLabelChange(e.target.value)}
            />
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Lo userai per riconoscere il computer nella lista. Puoi cambiarlo dopo.
            </div>
          </div>
        </>
      ) : (
        <div className="col gap-12">
          <div
            style={{
              fontFamily: "var(--mono)",
              fontSize: 36,
              letterSpacing: 6,
              textAlign: "center",
              padding: "18px 0",
              background: "var(--accent-dim)",
              borderRadius: 12,
              color: "var(--accent)",
              fontWeight: 600,
              userSelect: "all",
            }}
          >
            {pairing.pairing.code}
          </div>
          <div style={{ textAlign: "center" }}>
            <Btn disabled={expired || pairBusy} onClick={() => void copyCode()}>
              {copyState === "copied" && !expired ? "Copiato" : "Copia codice"}
            </Btn>
            {copyState === "copied" && !expired && <span role="status" className="muted" style={{ marginLeft: 8, fontSize: 12 }}>Codice copiato</span>}
            {copyState === "error" && !expired && <div role="alert" style={{ color: "var(--danger)", fontSize: 12, marginTop: 8 }}>Copia non riuscita. Seleziona il codice e copialo manualmente.</div>}
          </div>
          {pairLabel.trim() && <div className="muted" style={{ textAlign: "center", fontSize: 12 }}>Computer: {pairing.pairing.label}</div>}
          <div style={{ textAlign: "center", color: "var(--text-2)", fontSize: 13 }}>
            {pairing.remainingMs > 0
              ? `Scade tra ${Math.floor(pairing.remainingMs / 60_000)}:${String(Math.floor((pairing.remainingMs % 60_000) / 1000)).padStart(2, "0")}`
              : "Codice scaduto"}
          </div>
          <div className="preview-box" style={{ fontSize: 12, lineHeight: 1.6 }}>
            <strong>Nel popup Legacy:</strong>
            <ol style={{ paddingLeft: 16, margin: "6px 0 0" }}>
              <li>Premi l'icona Legacy in Chrome o Edge</li>
              <li>Incolla <span className="mono">{pairing.pairing.code}</span> in «Codice di collegamento»</li>
              <li>Premi «Collega dispositivo», poi chiudi questa finestra per vedere il computer nella lista.</li>
            </ol>
          </div>
        </div>
      )}
    </Modal>
  );
}
